import { isAnyOf } from '@reduxjs/toolkit'
import { enhancedTmsApiService } from '#api/enhancements/enhancedTmsApiService'
import type {
  EnhancedProductFamily,
  EnhancedProductFamilyId,
  EnhancedProductGroupId,
  EnhancedVariantList,
} from '#api/enhancements/enhancedTmsApiTypes'
import { selectMarketByConfiguration } from '#app/store/slices/configuration/marketSlice'
import { toaster } from '#features/Toasts/toaster'
import { getAllFamilies } from '#pages/Configuration/utils/collections'
import { calculateTotalConfigurationPrice } from '#utils/calculateTotalConfigurationPrice'
import type { Addition, Change, ConflictEntry, OptionChangeEntry, Removal } from '../../builders/configChangeTracker'
import {
  applyChange,
  clearChangeFailure,
  clearConflict,
  type LocalConfigurationId,
  modifyChange,
  setSidepanelFocus,
  setVariantsForFamily,
  type TruckConfiguration,
  type TruckConfigurations,
  truckConfigurationSlice,
} from '../../slices/configurationSlice'
import type { AppListenerApi, AppMiddleware } from '../configurationRequestMiddleware'

const getSelections = (nextPending: OptionChangeEntry): EnhancedVariantList => {
  if (nextPending.type === 'undo') {
    return [
      // undoing a selection means going back to the 'fromVariant'
      ...nextPending.selections.map((selection) => ({
        familyId: selection.family.id,
        variantId: selection.fromVariant.id,
      })),
      // undoing deselections means reselecting what has been deselected
      ...nextPending.deselections.map((deselection) => ({
        familyId: deselection.family.id,
        variantId: deselection.fromVariant.id,
      })),
      // all changes this caused have to be reselected
      ...nextPending.changes.map((x) => ({ familyId: x.family.id, variantId: x.toVariant.id })),
    ]
  }

  return nextPending.selections.map((selection) => ({
    familyId: selection.family.id,
    variantId: selection.toVariant.id,
  }))
}

const getDeselections = (nextPending: OptionChangeEntry): EnhancedVariantList => {
  if (nextPending.type === 'undo') {
    return [
      ...nextPending.selections
        .filter((x) => !x.wasUserSelectedVariant)
        .map((selection) => ({
          familyId: selection.family.id,
          variantId: selection.fromVariant.id,
        })),
      ...nextPending.changes.map((change) => ({ familyId: change.family.id, variantId: change.fromVariant.id })),
    ]
  }

  return [...nextPending.deselections.map((deselection) => ({ familyId: deselection.family.id, variantId: deselection.fromVariant.id }))]
}

const createRequest = async (
  listenerApi: AppListenerApi,
  nextPending: OptionChangeEntry,
  configuration: TruckConfiguration,
  checkedCompatibilityGroup: EnhancedProductGroupId | undefined,
) => {
  const selections = getSelections(nextPending)
  const deselections = getDeselections(nextPending)
  // no work to be done
  if (!configuration.uuid || (selections.length <= 0 && deselections.length <= 0)) {
    return [] as const
  }

  if (nextPending.type === 'revert') {
    const revertRequest = listenerApi.dispatch(
      enhancedTmsApiService.endpoints.postApiConfigurationByUuidUndocommit.initiate({
        uuid: configuration.uuid,
        configurationStepDataOptions: { returnCurrentConfiguration: true },
      }),
    )
    return [revertRequest] as const
  }

  // a selection first selects, then deselects if necessary
  if (selections.length > 0) {
    const selectionRequest = listenerApi.dispatch(
      enhancedTmsApiService.endpoints.postApiConfigurationByUuidSelectVariants.initiate({
        uuid: configuration.uuid,
        groupForCompatibilityCheck: checkedCompatibilityGroup,
        multiVariantSelectionRequest: {
          variantsForSelection: selections,
        },
      }),
    )
    const selectionResult = await selectionRequest

    // no more work to be done
    if (selectionResult.data === undefined || deselections.length <= 0) {
      return [selectionRequest] as const
    }

    // undo changes however can compute all things that can be deselected
    // this will inevitably create _more_ deselections, because the solver marks dependent changes as explicit, too
    const families = getAllFamilies(selectionResult.data.productGroups)
    const explicitFamilies = families.filter((x) => x.selectionType === 'Explicit')
    // any family without a configured selection will be marked to be deselected after reverting the configuration
    const familiesWithoutSelection = deselections.filter((deselection) =>
      explicitFamilies.some((explicitFamily) => deselection.familyId === explicitFamily.id),
    )
    if (familiesWithoutSelection.length <= 0) {
      return [selectionRequest] as const
    }

    const deselectionRequest = listenerApi.dispatch(
      enhancedTmsApiService.endpoints.postApiConfigurationByUuidDeselectVariants.initiate({
        uuid: configuration.uuid,
        groupForCompatibilityCheck: checkedCompatibilityGroup,
        multiVariantDeselectionRequest: {
          variantsForDeselection: deselections,
        },
      }),
    )
    const deselectResult = await deselectionRequest

    // observing for any problems - ideally should never happen
    if (deselectResult.data?.consequencesOfChange) {
      const variantConsequences = deselectResult.data.consequencesOfChange.variantConsequences ?? []
      const conflictingChoices = deselectResult.data.consequencesOfChange.variantConflictingChoices ?? []
      if (variantConsequences.length > 0 || conflictingChoices.length > 0) {
        toaster.add({
          appearance: 'warning',
          title: 'Deselection changed had implicit variant changes',
          message: 'Check the developer console for details',
        })
        console.warn(deselectResult.data)
      }
    }
    return [selectionRequest, deselectionRequest] as const
  }

  // a single deselection request just does the deselection
  const deselectionRequest = listenerApi.dispatch(
    enhancedTmsApiService.endpoints.postApiConfigurationByUuidDeselectVariants.initiate({
      uuid: configuration.uuid,
      groupForCompatibilityCheck: checkedCompatibilityGroup,
      multiVariantDeselectionRequest: {
        variantsForDeselection: deselections,
      },
    }),
  )

  return [deselectionRequest] as const
}

export const variantSelectionListener = (middleware: AppMiddleware) => {
  const doProcess = async (listenerApi: AppListenerApi, id: LocalConfigurationId, onExit: () => unknown) => {
    while (true) {
      const state = listenerApi.getState()
      const configuration = state.truckConfigurations.configurations[id]
      const history = configuration?.history
      // nothing to do
      if (!configuration?.uuid || !history) {
        break
      }
      // unresolved conflicts abort the request process
      if (history.some((x) => x.status === 'failed-unresolved')) {
        break
      }
      // unresolved conflicts do not allow further requests to be sent until resolved
      if (history.some((x) => x.status === 'success' && x.conflicts.length > 0)) {
        break
      }
      // find the next change to be processed
      const nextPending = history.find((x) => x.status === 'queued')
      if (!nextPending) {
        break
      }

      console.log('history step')

      // when selecting a change, overwrite the focus selection with the history to indicate it's doing something now
      listenerApi.dispatch(setSidepanelFocus({ id, focus: 'history' }))
      // enqueue and resolve requests
      const request = await createRequest(listenerApi, nextPending, configuration, configuration.compatibility?.focusGroup)
      const primaryRequest = request.at(0)
      if (primaryRequest === undefined) {
        throw new Error("There should've be at least on request that has been sent, but there was none.")
      }

      // mark the ticket as started
      listenerApi.dispatch(
        truckConfigurationSlice.actions._processConfigurationChange({
          process: nextPending.process,
          request: {
            id: primaryRequest.requestId,
            started: Date.now(),
          },
          id,
          selections: nextPending.selections.map((x) => ({ familyId: x.family.id, variantId: x.toVariant.id })),
        }),
      )

      try {
        const result = await primaryRequest.unwrap()
        const consequences = [
          ...(result.consequencesOfChange?.variantConsequences ?? []),
          ...(result.consequencesOfChange?.variantConflictingChoices ?? []),
        ]
          // filter out API changes that are the same as the user selection
          .filter(
            (x) =>
              !nextPending.selections.some(
                (selection) => selection.family.id === x.productFamily?.id && selection.toVariant.id === x.currentVariant?.id,
              ),
          )
        const configuration = state.truckConfigurations.configurations[id]
        const importantFamilies =
          selectMarketByConfiguration(state, configuration)?.attributes?.filter((x) => x.attributes.isImportant === true) ?? []

        const conflicts: ConflictEntry[] = []

        const addedFamilies = consequences
          .filter((x) => x.variantStatus === 'Added')
          .map((x) => {
            if (!x.productFamily?.id || !x.currentVariant?.id) {
              return undefined
            }
            const isImportant = importantFamilies.some((y) => y.code === x.productFamily?.id)

            const addition: Addition = {
              archetype: 'addition',
              family: {
                id: x.productFamily.id,
                title: x.productFamily.title,
              },
              toVariant: {
                id: x.currentVariant.id,
                title: x.currentVariant.title,
                price: x.currentVariant.dummy_price ?? -1,
              },
              isImportant,
            }
            return addition
          })
          .filter((x) => !!x)

        const changedFamilies = consequences
          .filter((x) => x.variantStatus === 'Changed')
          .map((x) => {
            if (!x.productFamily?.id || !x.currentVariant?.id || !x.previousVariant?.id) {
              return undefined
            }
            const isImportant = importantFamilies.some((y) => y.code === x.productFamily?.id)
            const change: Change = {
              archetype: 'change',
              family: {
                id: x.productFamily.id,
                title: x.productFamily.title,
              },
              fromVariant: {
                id: x.previousVariant.id,
                title: x.previousVariant.title,
                price: x.previousVariant.dummy_price ?? -1,
              },
              toVariant: {
                id: x.currentVariant.id,
                title: x.currentVariant.title,
                price: x.currentVariant.dummy_price ?? -1,
              },
              isImportant,
            }
            const priorSelection = configuration?.configuredFamilies[change.family.id]?.selectedVariant
            const priorSelectionTimestamp = priorSelection?.timestamp ?? Date.now()
            // if there has been a selection before and it's not what it got changed to, then this is a user conflict
            // so it gets inserted into the conflicts and not entered into the changed families
            if (isImportant || (priorSelection && priorSelection.id !== change.toVariant.id)) {
              conflicts.push({ ...change, lastSelectionTime: priorSelectionTimestamp })
              return undefined
            }
            return change
          })
          .filter((x) => !!x)

        const removedFamilies = consequences
          .filter((x) => x.variantStatus === 'Removed')
          .map((x) => {
            if (!x.productFamily?.id || !x.previousVariant?.id) {
              return undefined
            }
            const isImportant = importantFamilies.some((y) => y.code === x.productFamily?.id)
            const removal: Removal = {
              archetype: 'removal',
              family: {
                id: x.productFamily.id,
                title: x.productFamily.title,
              },
              fromVariant: {
                id: x.previousVariant.id,
                title: x.previousVariant.title,
                price: x.previousVariant.dummy_price ?? -1,
              },
              isImportant,
            }
            const priorSelection = configuration?.configuredFamilies[removal.family.id]?.selectedVariant
            const priorSelectionTimestamp = priorSelection?.timestamp ?? Date.now()
            // if there has been a selection before and it's not what it got changed to, then this is a user conflict
            // so it gets inserted into the conflicts and not entered into the changed families
            if (isImportant || priorSelection) {
              conflicts.push({ ...removal, lastSelectionTime: priorSelectionTimestamp })
              return undefined
            }
            return removal
          })
          .filter((x) => !!x)

        // the API might disagree what variant has been selected
        // - one example is that a motor selection can be selected once, but not reverted back after
        // thus the current user selections are checked against what's in the API and updated accordingly
        const families = getAllFamilies(result.productGroups)
        const lookup: Partial<Record<EnhancedProductFamilyId, EnhancedProductFamily>> = {}
        const errors: string[] = []
        for (const family of families) {
          lookup[family.id] = family
        }
        for (const familyId of Object.keys(configuration?.configuredFamilies ?? {})) {
          // conflicts are handled separately, they are of no concern
          if (conflicts.some((x) => x.family.id === familyId)) {
            continue
          }
          const enhancedFamilyId = familyId as EnhancedProductFamilyId
          const familySelection = configuration?.configuredFamilies[enhancedFamilyId]
          const userConfiguredVariant = familySelection?.selectedVariant
          if (!userConfiguredVariant || !familySelection) {
            continue
          }
          const productFamily = lookup[enhancedFamilyId]
          if (!productFamily) {
            continue
          }
          const apiConfiguredVariant = productFamily.variants.find((x) => !!x.isSelected)
          if (!apiConfiguredVariant) {
            // silently dropping selection entries for families that don't exist in the configuration anymore
            configuration.configuredFamilies[enhancedFamilyId] = { ...familySelection, selectedVariant: undefined }
            continue
          }
          if (userConfiguredVariant.id !== apiConfiguredVariant.id && configuration.configuredFamilies[enhancedFamilyId]) {
            errors.push(
              `- ${productFamily.title} User: ${productFamily.id}${userConfiguredVariant.id} | API: ${productFamily.id}${apiConfiguredVariant.id}`,
            )

            const originalVariant = productFamily.variants.find((x) => x.id === userConfiguredVariant.id)
            const isImportant = importantFamilies.some((y) => y.code === productFamily?.id)
            conflicts.push({
              archetype: 'change',
              family: {
                id: productFamily.id,
                title: productFamily.title,
              },
              fromVariant: {
                id: userConfiguredVariant.id,
                title: originalVariant?.title ?? 'unknown',
                price: originalVariant?.dummy_price ?? -1,
              },
              toVariant: {
                id: apiConfiguredVariant.id,
                title: apiConfiguredVariant.title,
                price: apiConfiguredVariant.dummy_price ?? -1,
              },
              lastSelectionTime: userConfiguredVariant.timestamp,
              isImportant: isImportant,
            })
          }
        }
        if (errors.length > 0) {
          toaster.add({
            appearance: 'error',
            title: 'User Choice could not be selected',
            message: `Some user selections could not be applied:\n${errors.join('\n')}`,
            visibleInMs: 10_000,
          })
        }

        const selectionPrices = nextPending.selections.map((x) => x.toVariant.price - (x.fromVariant?.price ?? 0))

        const selectionPrice = selectionPrices.reduce((a, b) => a + b, 0)
        const removedPrice = removedFamilies.map((x) => x.fromVariant.price).reduce((a, b) => a + b, 0)
        const changedPrice = changedFamilies.map((x) => x.toVariant.price - x.fromVariant.price).reduce((a, b) => a + b, 0)
        const priceDelta = changedPrice + selectionPrice - removedPrice

        const marketDetails = configuration ? state.truckConfigurations.marketData[configuration.apiConfiguration.market]?.details : undefined
        const priceSum = calculateTotalConfigurationPrice(result.productGroups, marketDetails, configuration?.configuredFamilies)

        listenerApi.dispatch(
          truckConfigurationSlice.actions._succeedConfigurationChange({
            conflicts: conflicts,
            consequences: {
              priceDelta: priceDelta,
              priceSum: priceSum,
              additions: addedFamilies,
              changes: changedFamilies,
              removals: removedFamilies,
            },
            process: nextPending.process,
            id,
            selections: nextPending.selections.map((x) => ({ familyId: x.family.id, variantId: x.toVariant.id })),
            checkedCompatibilityGroup: configuration?.compatibility?.focusGroup,
          }),
        )
      } catch {
        listenerApi.dispatch(
          truckConfigurationSlice.actions._failConfigurationChange({
            proposal: {
              id,
              selections: nextPending.selections.map((x) => ({ familyId: x.family.id, variantId: x.toVariant.id })),
            },
            request: await primaryRequest,
            process: nextPending.process,
          }),
        )
      }
    }

    onExit()
  }

  // const middleware = createListenerMiddleware()
  const tasks: Record<string, boolean> = {}
  // every time a change is enqueued, start the queue'd sender
  middleware.startListening({
    matcher: isAnyOf(setVariantsForFamily, clearChangeFailure, clearConflict, modifyChange, applyChange),
    effect: (_action, listenerApi) => {
      listenerApi.cancelActiveListeners()
      const state = listenerApi.getState()
      // Object.keys insists it always returns string[], thus removing the branded type
      const configurationKeys = Object.keys(state.truckConfigurations.configurations) as (keyof TruckConfigurations['configurations'])[]
      for (const key of configurationKeys) {
        if (key in tasks && tasks[key]) {
          continue
        }
        tasks[key] = true
        void doProcess(listenerApi, key, () => {
          tasks[key] = false
        })
      }
    },
  })
  return middleware
}
